import {
  type APIRboConfig,
  type DealFormData,
  dealFormField,
  dealStatus,
  rboConfigField,
  type ThirdPartyFeeConfig,
} from '@openx/types/deal';
import { ThirdPartyFeeConfigRevenueMethod } from '@openx/types/gqlTypes';

import { startOfMinute } from 'date-fns';
import { DealParticipantsField } from '../../components/DealParticipants/fields';
import { generatedIdTypes, idOptions } from '../sections/idsFormComponents/enums';

export const initDealParticipants = {
  [DealParticipantsField.BRAND_IDS]: [],
  [DealParticipantsField.BUYER_ID]: null,
  [DealParticipantsField.BUYER_IDS]: [],
  [DealParticipantsField.DSP]: null,
  [DealParticipantsField.MAIN_BUYER]: null,
  [DealParticipantsField.AMAZON_MARKETPLACE_ENABLED]: false,
};

export const initRboConfig: Required<APIRboConfig> = {
  [rboConfigField.HOURLY_TARGET]: null,
  [rboConfigField.DROP_PERCENT]: null,
  [rboConfigField.CHANNEL]: null,
  [rboConfigField.MODEL_GOAL]: null,
  [rboConfigField.TARGET]: null,
  [rboConfigField.THRESHOLD]: null,
  [rboConfigField.TACTIC]: null,
  [rboConfigField.CUSTOM_SCORE_NAME]: null,
  [rboConfigField.MODE]: null,
  initialized_at: null,
  message: null,
  status: null,
};

export const dealThirdPartyFeesConfigDefaultValues: ThirdPartyFeeConfig = {
  gross_cpm_cap: '',
  gross_share: '',
  partner_id: null,
  platform_partner_id: null,
  platform_share: '',
  revenue_method: ThirdPartyFeeConfigRevenueMethod.PoM,
};

export const dealDefaultInitialValues = {
  [dealFormField.ACCOUNT_MANAGER]: null,
  [dealFormField.ACCOUNT_EXECUTIVE]: null,
  [dealFormField.ACCOUNT_UID]: null,
  [dealFormField.CURRENCY]: null,
  [dealFormField.DEAL_PRICE]: '',
  [dealFormField.DEAL_SOURCE]: 'Publisher',
  [dealFormField.DEAL_SUPPLEMENTS]: null,
  [dealFormField.END_DATE]: null,
  [dealFormField.FIXED_CPM_ENABLED]: false,
  [dealFormField.EXPECTED_AD_CATEGORY]: null,
  [dealFormField.RBO_CONFIG]: null,
  [dealFormField.IMPRESSION_GOAL]: null,
  [dealFormField.NAME]: '',
  [dealFormField.OPEN_AUCTION_ACCESS]: '3',
  [dealFormField.PACKAGE_UID]: null,
  [dealFormField.PMP_DEAL_TYPE]: null,
  [dealFormField.START_DATE]: startOfMinute(new Date()),
  [dealFormField.STATUS]: dealStatus.Active,
  [dealFormField.USE_MARKET_FLOOR]: true,
  [dealFormField.DEAL_PARTICIPANTS]: [{ ...initDealParticipants }],
  [dealFormField.IS_PACING_ENABLED]: false,
  [dealFormField.HAS_MULTIPROVIDER_AUDIENCE]: false,
  [dealFormField.SEGMENT_PARTNER_REVENUE_METHOD]: null,
  [dealFormField.MONETIZING_BUDGET]: '',
  [dealFormField.PACING_MODE]: null,
  [dealFormField.MONETIZING_REGION]: null,
  [dealFormField.FREQUENCY_CAPPING]: [],
  [dealFormField.BUDGET_TYPE]: null,
  [dealFormField.BUDGET_MODE]: null,
  [dealFormField.MONETIZING_CADENCE_UNIT]: null,
  [dealFormField.PRICING_BY_ORG_DATA]: null,
  [dealFormField.CONTEXTUAL_MAX_POM]: null,
  [dealFormField.SEGMENT_PARTNER_PLATFORM_SHARE]: null,
  [dealFormField.PDP_CONFIG]: null,
  [dealFormField.AMAZON_CONFIG]: null,
  third_party_fees_config_enabled: false,
  third_party_fees_config: [dealThirdPartyFeesConfigDefaultValues],
} satisfies Partial<DealFormData>;

export const userDefinedIdFieldsInitialValues = {
  [dealFormField.CUSTOM_DEAL_ID]: '',
  [dealFormField.EXTERNAL_DEAL_ID]: '',
  [dealFormField.DEAL_ID_TYPE_RADIO]: idOptions.GENERATED,
  [dealFormField.GENERATED_DEAL_ID_PREFIX]: generatedIdTypes.Default,
};

export const keysToExcludeFromPayload = [
  'pricing_by_org_data',
  'contextual_max_pom',
  'has_multiprovider_audience',
  'revision',
  'modified_date',
  'v',
  '__meta',
  'targeting.account_uid',
];

/** Default cancellation period (days) for PDP Sync Programmatic Guaranteed deals. BE minimum is also 3. */
export const PDP_SYNC_PERIOD_DEFAULT = 3;
